/**
 * Iter-284f — TrustedHerd Merge Review admin page.
 *
 * Pulls collision candidates from /api/admin/import-collisions/trustedherd and
 * lets the admin pick one of the audited resolution actions for each row.
 * No automatic merges. Bulk actions only allow rows whose recommendation
 * matches the chosen action ("safe, same-action rows").
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Merge, UserX, ImagePlus, Mail, Phone, Briefcase, X, AlertTriangle, ShieldAlert,
  ChevronLeft, ChevronRight, Eye, Check,
} from 'lucide-react';
import api from '../../utils/api';
import { Link } from 'react-router-dom';

const CATEGORY_META = {
  email: { label: 'Email collision',  color: 'bg-amber-50 text-amber-700 border-amber-200' },
  phone: { label: 'Phone collision',  color: 'bg-amber-50 text-amber-700 border-amber-200' },
  split: { label: 'Split identity',   color: 'bg-rose-50 text-rose-700 border-rose-200' },
  name:  { label: 'Name match',       color: 'bg-violet-50 text-violet-700 border-violet-200' },
};

const ACTION_META = {
  merge_into_existing: { label: 'Merge into existing', icon: Merge,    color: 'bg-emerald-600 hover:bg-emerald-700' },
  keep_separate:       { label: 'Keep separate',       icon: Check,    color: 'bg-sky-600 hover:bg-sky-700' },
  attach_photos_only:  { label: 'Attach photos only',  icon: ImagePlus, color: 'bg-cyan-600 hover:bg-cyan-700' },
  update_email_only:   { label: 'Update missing email', icon: Mail,    color: 'bg-amber-600 hover:bg-amber-700' },
  update_phone_only:   { label: 'Update missing phone', icon: Phone,   color: 'bg-amber-600 hover:bg-amber-700' },
  convert_to_client:   { label: 'Convert to client',   icon: Briefcase, color: 'bg-violet-600 hover:bg-violet-700' },
  reject_import:       { label: 'Reject import',       icon: UserX,    color: 'bg-rose-600 hover:bg-rose-700' },
};

function Field({ label, current, incoming, conflict }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-xs py-1.5 border-b border-gray-100 last:border-b-0">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-400">{label} (iEvntür)</div>
        <div className={`font-medium ${current ? 'text-gray-900' : 'text-gray-300 italic'}`}>{current || 'empty'}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
          {label} (TH)
          {conflict && <ShieldAlert className="w-3 h-3 text-rose-600" />}
        </div>
        <div className={`font-medium ${conflict ? 'text-rose-700' : 'text-emerald-700'}`}>{incoming || 'empty'}</div>
      </div>
    </div>
  );
}

export default function MergeReview() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters
  const [kind, setKind] = useState('all');
  const [includeResolved, setIncludeResolved] = useState(false);
  const [search, setSearch] = useState('');

  // Detail dialog state
  const [activeRow, setActiveRow] = useState(null);
  const [auditRows, setAuditRows] = useState([]);

  // Bulk selection — only same-recommendation rows
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('kind', kind);
      if (includeResolved) params.set('include_resolved', 'true');
      params.set('limit', '500');
      const r = await api.get(`/admin/import-collisions/trustedherd?${params.toString()}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error('Failed to load collisions: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [kind, includeResolved]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const filteredRows = rows.filter(r => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (r.th_first_name || '').toLowerCase().includes(q) ||
      (r.th_last_name || '').toLowerCase().includes(q) ||
      (r.th_email || '').toLowerCase().includes(q) ||
      (r.external_worker_id || '').includes(q)
    );
  });

  const toggleSel = (wid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(wid)) next.delete(wid); else next.add(wid);
      return next;
    });
  };

  const handleResolve = async (row, action, matchedUserId, reason, override = false) => {
    try {
      const r = await api.post(`/admin/import-collisions/trustedherd/${row.external_worker_id}/resolve`, {
        action,
        matched_user_id: matchedUserId || null,
        reason: reason || null,
        override_recommendation: override,
      });
      toast.success(`${ACTION_META[action]?.label}: worker ${row.external_worker_id} — done`);
      return r.data;
    } catch (e) {
      toast.error('Action failed: ' + (e?.response?.data?.detail || e.message));
      return null;
    }
  };

  const handleBulk = async () => {
    if (!bulkAction) { toast.warning('Pick a bulk action first'); return; }
    if (selected.size === 0) { toast.warning('Select at least one row'); return; }

    // Safety: only allow bulk if every selected row has recommended_action === bulkAction
    const safeRows = filteredRows.filter(r => selected.has(r.external_worker_id));
    const unsafe = safeRows.filter(r => r.recommended_action !== bulkAction);
    if (unsafe.length > 0) {
      toast.error(`${unsafe.length} selected row(s) have a different recommended action. Resolve those individually.`);
      return;
    }
    if (['merge_into_existing', 'attach_photos_only'].includes(bulkAction)) {
      toast.error('Merge and Attach Photos require a per-row matched user choice — use the row dialog.');
      return;
    }
    const reason = window.prompt(
      `Apply "${ACTION_META[bulkAction].label}" to ${safeRows.length} row(s)? Optional reason (audited):`,
      ''
    );
    if (reason === null) return;
    let ok = 0;
    for (const r of safeRows) {
      const res = await handleResolve(r, bulkAction, null, reason || null);
      if (res) ok += 1;
    }
    toast.success(`Bulk: ${ok}/${safeRows.length} resolved`);
    setSelected(new Set());
    setBulkAction('');
    await fetchList();
  };

  const openRow = async (row) => {
    setActiveRow(row);
    setAuditRows([]);
    try {
      const r = await api.get(`/admin/import-collisions/trustedherd/audit?external_worker_id=${row.external_worker_id}`);
      setAuditRows(r.data.rows || []);
    } catch (e) {
      // non-fatal
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6" data-testid="merge-review-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Merge Review</h1>
          <p className="text-sm text-gray-500 mt-1">
            Resolve TrustedHerd import collisions. Every action is audit-logged.
            Safety: <strong>never auto-merge</strong>; bulk actions only allow same-recommendation rows.
          </p>
        </div>
        <Link to="/admin/users" className="text-sm text-cyan-700 hover:underline">← Back to Users</Link>
      </div>

      {/* Stats / filters */}
      <Card className="mb-4">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Category</label>
            <Select value={kind} onValueChange={v => setKind(v)}>
              <SelectTrigger data-testid="filter-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                <SelectItem value="email">Email collision</SelectItem>
                <SelectItem value="phone">Phone collision</SelectItem>
                <SelectItem value="split">Split identity</SelectItem>
                <SelectItem value="name">Name match</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Search (name / email / worker id)</label>
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="e.g. kelly@gmail.com" data-testid="merge-search" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">View</label>
            <Select value={includeResolved ? 'all' : 'pending'} onValueChange={v => setIncludeResolved(v === 'all')}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending only</SelectItem>
                <SelectItem value="all">All (incl. resolved)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Badge variant="outline" className="text-sm px-3 py-1.5" data-testid="pending-total">{total} pending</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Bulk bar */}
      <Card className="mb-4">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <Badge variant="outline" data-testid="selected-count">{selected.size} selected</Badge>
          <Select value={bulkAction} onValueChange={setBulkAction}>
            <SelectTrigger className="w-60" data-testid="bulk-action-select">
              <SelectValue placeholder="Bulk action…" />
            </SelectTrigger>
            <SelectContent>
              {/* Safe-only bulk actions; merge/attach require per-row match */}
              <SelectItem value="keep_separate">Keep separate</SelectItem>
              <SelectItem value="update_email_only">Update missing email</SelectItem>
              <SelectItem value="update_phone_only">Update missing phone</SelectItem>
              <SelectItem value="convert_to_client">Convert to client</SelectItem>
              <SelectItem value="reject_import">Reject import</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={handleBulk} data-testid="apply-bulk-btn">Apply to safe rows</Button>
          <div className="flex-1" />
          <span className="text-xs text-gray-500">
            <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-500" />
            Bulk skips rows whose recommendation differs from the chosen action.
          </span>
        </CardContent>
      </Card>

      {/* Rows */}
      <div className="space-y-2">
        {loading && rows.length === 0 && (
          <div className="text-center py-12 text-gray-500">Loading collisions…</div>
        )}
        {!loading && filteredRows.length === 0 && (
          <div className="text-center py-12 text-gray-500">No collisions match the current filters.</div>
        )}
        {filteredRows.map(r => {
          const cat = CATEGORY_META[r.category] || CATEGORY_META.name;
          const isSelected = selected.has(r.external_worker_id);
          return (
            <Card key={r.external_worker_id} className={`${isSelected ? 'border-cyan-500 border-2' : ''}`} data-testid={`row-${r.external_worker_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1 w-4 h-4 accent-cyan-600 cursor-pointer"
                    checked={isSelected}
                    onChange={() => toggleSel(r.external_worker_id)}
                    data-testid={`select-${r.external_worker_id}`}
                  />
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* TH side */}
                    <div>
                      <div className="text-xs font-semibold text-gray-700 mb-1">
                        TrustedHerd #{r.external_worker_id}
                        <span className={`ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded border ${cat.color}`}>{cat.label}</span>
                      </div>
                      <div className="text-sm font-semibold" data-testid={`th-name-${r.external_worker_id}`}>{r.th_first_name} {r.th_last_name}</div>
                      <div className="text-xs text-gray-500">{r.th_email}</div>
                      <div className="text-xs text-gray-500">{r.th_phone}</div>
                    </div>

                    {/* Comparison */}
                    <div className="bg-gray-50 rounded p-2">
                      <Field label="Email" current={r.iev_current_email} incoming={r.th_email} conflict={!!r.match_by_email} />
                      <Field label="Phone" current={r.iev_current_phone} incoming={r.th_phone} conflict={!!r.match_by_phone} />
                      <Field label="Name match" current="-" incoming={r.match_by_name ? `${r.match_by_name.user_id?.slice(0,8)}…` : '-'} />
                    </div>

                    {/* Matched user */}
                    <div className="text-xs">
                      <div className="text-gray-500 mb-1">Matched iEvntür user(s)</div>
                      {r.match_by_email && (
                        <div className="mb-1 p-1.5 bg-amber-50 rounded">
                          <div className="font-mono text-[10px]">{r.match_by_email.user_id}</div>
                          <div>{r.match_by_email.email} · <Badge variant="outline">{r.match_by_email.role}</Badge></div>
                        </div>
                      )}
                      {r.match_by_phone && r.match_by_phone.user_id !== r.match_by_email?.user_id && (
                        <div className="mb-1 p-1.5 bg-rose-50 rounded">
                          <div className="font-mono text-[10px]">{r.match_by_phone.user_id}</div>
                          <div>{r.match_by_phone.phone} · <Badge variant="outline">{r.match_by_phone.role}</Badge></div>
                        </div>
                      )}
                      <Badge variant="secondary" className="mt-1" data-testid={`recommended-${r.external_worker_id}`}>
                        Recommended: {ACTION_META[r.recommended_action]?.label || r.recommended_action}
                      </Badge>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => openRow(r)} data-testid={`open-${r.external_worker_id}`}>
                    <Eye className="w-4 h-4 mr-1" /> Resolve
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Per-row resolve dialog */}
      <Dialog open={!!activeRow} onOpenChange={(open) => { if (!open) setActiveRow(null); }}>
        <DialogContent
          className="!grid-cols-none max-w-3xl w-[calc(100vw-1rem)] max-h-[calc(100dvh-2rem)] !flex !flex-col p-0 gap-0"
          data-testid="merge-resolve-dialog"
        >
          <DialogHeader className="p-4 sm:p-6 pb-3 border-b border-gray-100 flex-shrink-0">
            <DialogTitle className="text-base sm:text-lg pr-6">
              Resolve worker #{activeRow?.external_worker_id} — {activeRow?.th_first_name} {activeRow?.th_last_name}
            </DialogTitle>
          </DialogHeader>
          {activeRow && (
            <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6" data-testid="merge-resolve-scroll">
              <ResolveDialog
                row={activeRow}
                auditRows={auditRows}
                onResolve={async (action, matchedId, reason, override) => {
                  const res = await handleResolve(activeRow, action, matchedId, reason, override);
                  if (res) {
                    setActiveRow(null);
                    await fetchList();
                  }
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResolveDialog({ row, auditRows, onResolve }) {
  const recommended = row.recommended_action;
  const [chosen, setChosen] = useState(recommended);
  // Iter-326r — Pre-populate matched user so admins never have to type UUIDs.
  const suggestedMatch = row.match_by_email?.user_id || row.match_by_phone?.user_id || row.match_by_name?.user_id || '';
  const [matchedId, setMatchedId] = useState(suggestedMatch);
  const [reason, setReason] = useState('');
  // Iter-326u — Explicit "Force replace populated value" toggle so admins
  // can overwrite bad legacy data (e.g. phone column holding "Mcdavo699")
  // with the TrustedHerd value, not just fill blanks. Every force-replace
  // is audit-logged with override=true.
  const [forceReplace, setForceReplace] = useState(false);
  const isForceableAction = ['update_email_only', 'update_phone_only'].includes(chosen);
  const isOverride = chosen !== recommended || forceReplace;
  const needsMatched = ['merge_into_existing', 'attach_photos_only', 'update_email_only', 'update_phone_only'].includes(chosen);

  // Detect whether the matched user's target field is currently populated
  // so we can prompt the admin to enable force-replace instead of hitting
  // a confusing 409 error mid-workflow.
  const matchedUserForForce = row.match_by_email || row.match_by_phone || null;
  const targetFieldPopulated = (() => {
    if (!matchedUserForForce || !isForceableAction || !matchedId) return false;
    if (chosen === 'update_phone_only') return !!matchedUserForForce.phone;
    if (chosen === 'update_email_only') return !!matchedUserForForce.email;
    return false;
  })();

  // Pick the matched user object (email match wins, phone match second, name match last).
  const matchedUser = row.match_by_email || row.match_by_phone || null;
  const matchedName = matchedUser?.role ? `existing ${matchedUser.role}` : 'existing iEvntür user';

  // Per-action outcome preview so admins see exactly what will change.
  const outcome = describeOutcome(chosen, row, matchedUser);

  return (
    <div className="space-y-4">
      {/* ── Side-by-side record comparison ─────────────────────────── */}
      <div className="rounded border border-gray-200 overflow-hidden" data-testid="merge-comparison">
        <div className="grid grid-cols-2 divide-x divide-gray-200 bg-gray-50 text-xs">
          <div className="p-3">
            <div className="uppercase tracking-wider text-gray-500 font-bold">iEvntür ({matchedName})</div>
            <div className="text-[10px] text-gray-400 font-mono mt-0.5">
              {matchedUser?.user_id ? matchedUser.user_id.slice(0, 12) + '…' : 'no match'}
            </div>
          </div>
          <div className="p-3">
            <div className="uppercase tracking-wider text-cyan-700 font-bold">TrustedHerd (incoming)</div>
            <div className="text-[10px] text-gray-400 font-mono mt-0.5">worker #{row.external_worker_id}</div>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          <MergeRow
            label="Name"
            iev={matchedUser ? `${matchedUser.first_name || ''} ${matchedUser.last_name || ''}`.trim() || '—' : '—'}
            th={`${row.th_first_name || ''} ${row.th_last_name || ''}`.trim() || '—'}
          />
          <MergeRow
            label="Email"
            iev={matchedUser?.email || ''}
            th={row.th_email || ''}
          />
          <MergeRow
            label="Phone"
            iev={matchedUser?.phone || ''}
            th={row.th_phone || ''}
          />
          <MergeRow
            label="Role"
            iev={matchedUser?.role || '—'}
            th="talent (import)"
            neutral
          />
        </div>
      </div>

      {/* ── Recommended banner ─────────────────────────────────────── */}
      {recommended && recommended !== 'manual_review' && (
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2.5 text-emerald-900" data-testid="merge-recommendation">
          <span className="font-bold uppercase tracking-wider text-emerald-700 text-[10px]">Recommended · </span>
          {ACTION_META[recommended]?.label || recommended}
          {matchedUser && (
            <span className="text-emerald-700"> → into {matchedUser.email || matchedUser.phone || matchedUser.user_id?.slice(0, 8)}</span>
          )}
        </div>
      )}
      {recommended === 'manual_review' && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2.5 text-amber-900">
          <span className="font-bold uppercase tracking-wider text-amber-700 text-[10px]">Manual review · </span>
          No safe automatic action detected. Pick the outcome that matches the data above.
        </div>
      )}

      {/* ── Action selector ────────────────────────────────────────── */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Choose an action</div>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(ACTION_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const isChosen = chosen === key;
            const isRec = recommended === key;
            return (
              <button
                key={key}
                onClick={() => setChosen(key)}
                className={`flex items-center gap-2 p-2 rounded border text-left text-sm ${isChosen ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200'}`}
                data-testid={`action-${key}`}
              >
                <Icon className="w-4 h-4" />
                <div className="flex-1">{meta.label}</div>
                {isRec && <Badge variant="outline" className="text-[10px]">recommended</Badge>}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Matched user selector (only when action needs one) ─────── */}
      {needsMatched && (
        <div>
          <label className="text-xs text-gray-500 block mb-1">
            {['update_email_only', 'update_phone_only'].includes(chosen)
              ? 'Matched iEvntür user (leave blank to backfill TH row only)'
              : 'Merge into user_id (required)'}
          </label>
          <Input
            value={matchedId}
            onChange={e => setMatchedId(e.target.value)}
            placeholder="UUID"
            data-testid="matched-user-input"
          />
          <div className="text-xs text-gray-400 mt-1">
            Suggested:&nbsp;
            {row.match_by_email?.user_id && (
              <button className="font-mono underline" onClick={() => setMatchedId(row.match_by_email.user_id)}>
                {row.match_by_email.user_id.slice(0,8)}… (email match)
              </button>
            )}
            {row.match_by_phone?.user_id && row.match_by_phone.user_id !== row.match_by_email?.user_id && (
              <>
                {' · '}
                <button className="font-mono underline" onClick={() => setMatchedId(row.match_by_phone.user_id)}>
                  {row.match_by_phone.user_id.slice(0,8)}… (phone match)
                </button>
              </>
            )}
            {row.match_by_name?.user_id && ![row.match_by_email?.user_id, row.match_by_phone?.user_id].includes(row.match_by_name.user_id) && (
              <>
                {' · '}
                <button className="font-mono underline" onClick={() => setMatchedId(row.match_by_name.user_id)}>
                  {row.match_by_name.user_id.slice(0,8)}… (name match)
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Iter-326u — Force-replace toggle for correcting bad data */}
      {isForceableAction && targetFieldPopulated && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="force-replace-panel">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-900 flex-1">
              <div className="font-bold">Matched user already has {chosen === 'update_phone_only' ? 'a phone' : 'an email'}:</div>
              <div className="font-mono mt-0.5">
                {chosen === 'update_phone_only' ? matchedUserForForce?.phone : matchedUserForForce?.email}
              </div>
              <div className="mt-1">
                If this existing value is <b>incorrect</b> (e.g. legacy garbage data like <code>Mcdavo699</code>),
                enable the toggle below to <b>replace it</b> with the TrustedHerd value. Every replace is audit-logged.
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer pl-6">
            <input
              type="checkbox"
              checked={forceReplace}
              onChange={e => setForceReplace(e.target.checked)}
              data-testid="force-replace-toggle"
              className="w-4 h-4"
            />
            <span className="text-xs font-semibold text-amber-900">
              Force replace: overwrite the existing populated value (audited)
            </span>
          </label>
        </div>
      )}

      {/* ── Outcome preview ────────────────────────────────────────── */}
      {outcome && (
        <div className="text-xs bg-cyan-50 border border-cyan-200 rounded p-2.5 text-cyan-900" data-testid="merge-outcome">
          <span className="font-bold uppercase tracking-wider text-cyan-700 text-[10px]">This will · </span>
          {outcome}
        </div>
      )}

      <div>
        <label className="text-xs text-gray-500 block mb-1">Reason / note (audited)</label>
        <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. confirmed via phone call" data-testid="resolve-reason" />
      </div>

      {isOverride && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded p-2 border border-amber-200">
          <AlertTriangle className="w-4 h-4" />
          You are overriding the recommended action ({ACTION_META[recommended]?.label || recommended}).
          The audit row will be flagged with <code>override=true</code>.
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => onResolve(null)}><X className="w-4 h-4 mr-1" /> Cancel</Button>
        <Button
          className={ACTION_META[chosen]?.color + ' text-white'}
          onClick={() => onResolve(chosen, matchedId || null, reason, isOverride)}
          disabled={isForceableAction && targetFieldPopulated && !forceReplace}
          data-testid="confirm-resolve"
        >
          {forceReplace ? 'Force replace' : `Apply: ${ACTION_META[chosen]?.label}`}
        </Button>
      </div>

      {auditRows.length > 0 && (
        <div className="pt-3 border-t">
          <div className="text-xs text-gray-500 mb-2">Audit history ({auditRows.length})</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {auditRows.map(a => (
              <div key={a.id} className="text-xs flex items-center justify-between border rounded p-2" data-testid={`audit-row-${a.id}`}>
                <span>{a.action} by {a.actor_display || a.actor_email}</span>
                <span className="text-gray-400">{new Date(a.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Iter-326r — Side-by-side field row with a visual "will fill" indicator when
// iEvntür is empty and TH has a value.
function MergeRow({ label, iev, th, neutral }) {
  const ievEmpty = !iev || iev.trim() === '';
  const thHas = !!th && th.trim() !== '';
  const willFill = !neutral && ievEmpty && thHas;
  const conflict = !neutral && !ievEmpty && thHas && iev !== th;
  return (
    <div className="grid grid-cols-2 divide-x divide-gray-100 text-xs">
      <div className={`p-2.5 ${willFill ? 'bg-emerald-50/50' : ''}`}>
        <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
        <div className={`font-medium ${ievEmpty ? 'text-gray-300 italic' : 'text-gray-900'}`}>
          {iev || 'empty'}
          {willFill && <span className="ml-2 text-emerald-700 text-[10px] font-bold">← will fill from TH</span>}
        </div>
      </div>
      <div className={`p-2.5 ${conflict ? 'bg-rose-50/50' : ''}`}>
        <div className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
          {label}
          {conflict && <ShieldAlert className="w-3 h-3 text-rose-600" />}
        </div>
        <div className={`font-medium ${!thHas ? 'text-gray-300 italic' : conflict ? 'text-rose-700' : 'text-emerald-700'}`}>
          {th || 'empty'}
        </div>
      </div>
    </div>
  );
}

function describeOutcome(action, row, matchedUser) {
  const th = { email: row.th_email, phone: row.th_phone };
  switch (action) {
    case 'merge_into_existing':
      if (!matchedUser) return 'Select a matched iEvntür user to merge into.';
      return `Copy TH's photos + missing email/phone onto ${matchedUser.email || matchedUser.user_id?.slice(0, 8)} · then archive the TH-imported record. No existing values will be overwritten.`;
    case 'attach_photos_only':
      if (!matchedUser) return 'Select a matched iEvntür user.';
      return `Move only TH's talent_media to ${matchedUser.email || matchedUser.user_id?.slice(0, 8)}. No PII changes.`;
    case 'update_email_only':
      if (matchedUser) return `Fill blank email on ${matchedUser.email || matchedUser.user_id?.slice(0, 8)} with "${th.email}". Refuses if matched user already has an email.`;
      return `Backfill only the TH-imported user's blank email from PII. No changes to any other record.`;
    case 'update_phone_only':
      if (matchedUser) return `Fill blank phone on ${matchedUser.email || matchedUser.user_id?.slice(0, 8)} with "${th.phone}". Refuses if matched user already has a phone.`;
      return `Backfill only the TH-imported user's blank phone from PII. No changes to any other record.`;
    case 'keep_separate':
      return 'Mark this collision as resolved and keep both records separate. No data changes.';
    case 'convert_to_client':
      return 'Convert the TH-imported user into a client account. Roles are switched, no PII changes.';
    case 'reject_import':
      return 'Archive the TH-imported user permanently. They will not appear anywhere in the app.';
    default:
      return '';
  }
}
